import {
  Box,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import {
  getStoreGroupReportFlow,
  getStoreGroupReportOrders,
  getStoreGroupReportStores,
  getStoreGroupReportSummary,
} from 'services/storeGroupReport';
import type {
  ReportPeriod,
  StoreGroupReportFlow,
  StoreGroupReportOrders,
  StoreGroupReportStores,
  StoreGroupReportSummary,
} from 'services/interfaces/storeGroupReport';
import PaginationFooter from 'shared/components/Logs/PaginationFooter';
import ReportFlowPanel from './ReportFlowPanel';
import ReportKpiCards from './ReportKpiCards';
import ReportOrdersTable from './ReportOrdersTable';
import ReportPeriodFilter from './ReportPeriodFilter';
import ReportStoresRankingTable from './ReportStoresRankingTable';

const PER_PAGE = 20;

interface GroupReportTabProps {
  storeGroupId: number;
}

// Réplica do relatório de rede do ionic (network-report-tab), sem a sub-lista de clientes
// (fica exclusiva da aba Clientes desta fatia) e sem o donut do fluxo (KPI cards em vez de
// gráfico — mesmo padrão do resto do react-customers).
const GroupReportTab: React.FC<GroupReportTabProps> = ({ storeGroupId }) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  const [period, setPeriod] = useState<ReportPeriod | null>(null);
  const [selectedIdi, setSelectedIdi] = useState('');

  const [summary, setSummary] = useState<StoreGroupReportSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(false);

  const [orders, setOrders] = useState<StoreGroupReportOrders | null>(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [stores, setStores] = useState<StoreGroupReportStores | null>(null);
  const [storesPage, setStoresPage] = useState(1);
  const [storesLoading, setStoresLoading] = useState(false);

  const [flow, setFlow] = useState<StoreGroupReportFlow | null>(null);
  const [flowLoading, setFlowLoading] = useState(false);

  const loadSummary = useCallback(
    async (p: ReportPeriod, idi: string) => {
      setSummaryLoading(true);
      setSummaryError(false);
      try {
        const res = await getStoreGroupReportSummary(
          storeGroupId,
          p,
          idi || undefined,
        );
        setSummary(res);
      } catch {
        setSummaryError(true);
      } finally {
        setSummaryLoading(false);
      }
    },
    [storeGroupId],
  );

  const loadOrders = useCallback(
    async (p: ReportPeriod, idi: string, page: number) => {
      setOrdersLoading(true);
      try {
        const res = await getStoreGroupReportOrders(
          storeGroupId,
          p,
          { page, perPage: PER_PAGE, sort: 'date', dir: 'desc' },
          idi || undefined,
        );
        setOrders(res);
      } finally {
        setOrdersLoading(false);
      }
    },
    [storeGroupId],
  );

  const loadStores = useCallback(
    async (p: ReportPeriod, page: number) => {
      setStoresLoading(true);
      try {
        const res = await getStoreGroupReportStores(storeGroupId, p, {
          page,
          perPage: PER_PAGE,
          sort: 'generated',
          dir: 'desc',
        });
        setStores(res);
      } finally {
        setStoresLoading(false);
      }
    },
    [storeGroupId],
  );

  const loadFlow = useCallback(
    async (p: ReportPeriod) => {
      setFlowLoading(true);
      try {
        const res = await getStoreGroupReportFlow(storeGroupId, p);
        setFlow(res);
      } finally {
        setFlowLoading(false);
      }
    },
    [storeGroupId],
  );

  // Período novo refaz os quatro blocos.
  const handlePeriodApply = (p: ReportPeriod) => {
    setPeriod(p);
    setOrdersPage(1);
    setStoresPage(1);
    loadSummary(p, selectedIdi);
    loadOrders(p, selectedIdi, 1);
    loadStores(p, 1);
    loadFlow(p);
  };

  // Loja trocada refaz só summary e orders — ranking e fluxo comparam lojas entre si.
  const handleStoreChange = (idi: string) => {
    setSelectedIdi(idi);
    if (!period) return;
    setOrdersPage(1);
    loadSummary(period, idi);
    loadOrders(period, idi, 1);
  };

  useEffect(() => {
    if (!period) return;
    loadOrders(period, selectedIdi, ordersPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersPage]);

  useEffect(() => {
    if (!period) return;
    loadStores(period, storesPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesPage]);

  const ordersTotalPages = orders?.pagination.totalPages ?? 1;
  const storesTotalPages = stores?.pagination.totalPages ?? 1;

  return (
    <Stack spacing={6}>
      <HStack justify="space-between" flexWrap="wrap" gap={3}>
        <ReportPeriodFilter onApply={handlePeriodApply} />

        {summary?.stores && summary.stores.length > 0 && (
          <Select
            w="auto"
            size="sm"
            value={selectedIdi}
            onChange={(e) => handleStoreChange(e.target.value)}
          >
            <option value="">Todas as lojas</option>
            {summary.stores.map((store) => (
              <option key={store.idi} value={store.idi}>
                {store.name}
              </option>
            ))}
          </Select>
        )}
      </HStack>

      {summaryLoading && (
        <HStack justify="center" py="2rem">
          <Spinner color="yooga.500" />
          <Text>Carregando…</Text>
        </HStack>
      )}

      {summaryError && (
        <Text color="red.500">Não foi possível carregar o resumo.</Text>
      )}

      {summary && !summaryLoading && <ReportKpiCards summary={summary} />}

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Heading size="sm" color="gray.600" mb={4}>
          Pedidos
        </Heading>
        {ordersLoading ? (
          <HStack justify="center" py="2rem">
            <Spinner color="yooga.500" />
          </HStack>
        ) : (
          orders && (
            <Stack spacing={4}>
              <ReportOrdersTable data={orders.data} />
              {ordersTotalPages > 1 && (
                <PaginationFooter
                  visibleLength={orders.pagination.totalItems}
                  page={ordersPage}
                  setPage={setOrdersPage}
                  pageCount={ordersTotalPages}
                />
              )}
            </Stack>
          )
        )}
      </Box>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Heading size="sm" color="gray.600" mb={4}>
          Ranking de lojas
        </Heading>
        {storesLoading ? (
          <HStack justify="center" py="2rem">
            <Spinner color="yooga.500" />
          </HStack>
        ) : (
          stores && (
            <Stack spacing={4}>
              <ReportStoresRankingTable data={stores.data} />
              {storesTotalPages > 1 && (
                <PaginationFooter
                  visibleLength={stores.pagination.totalItems}
                  page={storesPage}
                  setPage={setStoresPage}
                  pageCount={storesTotalPages}
                />
              )}
            </Stack>
          )
        )}
      </Box>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Heading size="sm" color="gray.600" mb={4}>
          Fluxo entre lojas
        </Heading>
        {flowLoading ? (
          <HStack justify="center" py="2rem">
            <Spinner color="yooga.500" />
          </HStack>
        ) : (
          flow && <ReportFlowPanel flow={flow} />
        )}
      </Box>
    </Stack>
  );
};

export default GroupReportTab;
